// Local Storage API for project management
import { generateId } from '@/utils';

const STORAGE_KEY = 'sno_lighting_projects';

// Get all projects from localStorage
function getProjects() {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : [];
}

// Save projects to localStorage
function saveProjects(projects) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

// Entity API for Projects
const ProjectEntity = {
  // List all projects with optional sorting
  list: async (sortField = '-created_date') => {
    const projects = getProjects();
    
    // Simple sorting by created_date
    if (sortField === '-created_date') {
      return projects.sort((a, b) => 
        new Date(b.created_date) - new Date(a.created_date)
      );
    }
    
    return projects;
  },

  // Get a single project by ID
  get: async (id) => {
    const projects = getProjects();
    const project = projects.find(p => p.id === id);
    if (!project) {
      throw new Error('Project not found');
    }
    return project;
  },

  // Create a new project
  create: async (data) => {
    const projects = getProjects();
    const newProject = {
      ...data,
      id: generateId(),
      created_date: new Date().toISOString(),
      updated_date: new Date().toISOString(),
      status: 'draft',
      sections: [],
    };
    projects.push(newProject);
    saveProjects(projects);
    return newProject;
  },

  // Update an existing project
  update: async (id, data) => {
    const projects = getProjects();
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) {
      throw new Error('Project not found');
    }
    projects[index] = {
      ...projects[index],
      ...data,
      updated_date: new Date().toISOString(),
    };
    saveProjects(projects);
    return projects[index];
  },

  // Delete a project
  delete: async (id) => {
    const projects = getProjects();
    const filtered = projects.filter(p => p.id !== id);
    saveProjects(filtered);
    return { success: true };
  },
};

// File upload integration
const CoreIntegration = {
  // Convert file to base64 data URL for local storage
  UploadFile: async ({ file }) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({ file_url: e.target.result });
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  },
};

// Export local API
export const localApi = {
  entities: {
    Project: ProjectEntity,
  },
  integrations: {
    Core: CoreIntegration,
  },
};
